import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, tap, catchError } from 'rxjs/operators';
import { RestfulApiService } from './restful-api.service';
import { convertTwoToThreeLetterCode } from '../constants/language-codes';
import { DynamicTranslationService } from './dynamic-translation.service';

export interface Language {
  code: string;
  name: string;
  flag?: string;
  uuid?: string;
  state?: 'DRAFT' | 'LIVE';
  countryName?: string;
}

export interface LanguageTranslationItem {
  labelKey: string;
  description: string | null;
  selectedLocaleValue: string;
  defaultLocaleValue: string;
}

export interface LanguageTranslationsResponse {
  response: {
    pageNumber: number;
    objects: LanguageTranslationItem[];
    totalObjects: number;
    from: number;
    to: number;
  };
}

export interface LanguageTranslations {
  [key: string]: string;
}

/**
 * Language search response from API
 */
export interface LanguageSearchResponse {
  response: {
    [key: string]: Array<{
      country: string;
      flag: string;
      language: string;
    }>;
  };
}

/**
 * Payload for creating or updating a language
 */
export interface CreateUpdateLanguagePayload {
  countryName: string;
  languageCode: string;
  state: 'DRAFT' | 'LIVE';
  uuid?: string | null;
}

/**
 * Language entry returned by the languages-with-usage endpoint
 */
export interface LanguageWithUsage {
  uuid: string;
  code: string;
  name: string;
  flag: string;
  state: 'LIVE' | 'DRAFT';
  lastModified: number;
  hasActiveConnections: boolean;
}

/**
 * Response shape for GET /v1/admin/languages-with-usage
 */
export interface LanguagesWithUsageResponse {
  response: LanguageWithUsage[];
}

@Injectable({
  providedIn: 'root'
})
export class LanguageService {
  private readonly STORAGE_KEY = 'selectedLanguage';
  private readonly TRANSLATIONS_STORAGE_KEY_PREFIX = 'languageTranslations';
  private readonly TRANSLATIONS_TIMESTAMP_KEY_PREFIX = 'languageTranslationsTimestamp';
  private readonly TRANSLATIONS_CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

  languages: Language[] = [
    { code: 'eng', name: 'English', flag: '🇬🇧' },
    { code: 'deu', name: 'Deutsch', flag: '🇩🇪' }
  ];

  private currentLanguageSubject = new BehaviorSubject<Language>(
    this.getStoredLanguage() || this.languages[0]
  );

  private translationsSubject = new BehaviorSubject<LanguageTranslations>({});

  currentLanguage$ = this.currentLanguageSubject.asObservable();
  translations$ = this.translationsSubject.asObservable();

  constructor(
    private restfulApiService: RestfulApiService,
    private dynamicTranslationService: DynamicTranslationService
  ) {
    // Initialize the current language without triggering a redirect
    const stored = this.getStoredLanguage();
    if (stored) {
      this.currentLanguageSubject.next(stored);
    }

    // Initialize translations for the current language after the current language is set
    const currentLang = this.getCurrentLanguage();
    const locale = convertTwoToThreeLetterCode(currentLang.code);
    const storedTranslations = this.getStoredTranslations(locale);
    this.translationsSubject.next(storedTranslations);

    // NOTE: Removed automatic initialization to prevent duplicate API calls
    // Translations are now loaded by DynamicTranslationService in APP_INITIALIZER
    // Sync translations from DynamicTranslationService to this service
    this.dynamicTranslationService.translations$.subscribe(translations => {
      if (translations && Object.keys(translations).length > 0) {
        this.translationsSubject.next(translations);
      }
    });
  }

  getCurrentLanguage(): Language {
    return this.currentLanguageSubject.value;
  }

  setLanguage(language: Language): void {
    this.currentLanguageSubject.next(language);
    localStorage.setItem(this.STORAGE_KEY, language.code);

    const baseHref = document.querySelector('base')?.getAttribute('href') || '/';
    const currentPath = window.location.pathname.replace(baseHref, '');

    window.location.href = `/${language.code}/${currentPath}`;
  }

  getLanguageTranslations(): Observable<LanguageTranslations> {
    // Get current locale from the current language
    const currentLang = this.getCurrentLanguage();
    const locale = convertTwoToThreeLetterCode(currentLang.code);

    return this.getLanguageTranslationsForLocale(locale);
  }

  getLanguageTranslationsForLocale(locale: string, appType?: 'GS' | 'PORTAL' | null): Observable<LanguageTranslations> {
    // Build URL with appType query parameter if provided
    let url = `/v1/language/translations/${locale}`;
    if (appType) {
      url += `?appType=${appType}`;
    }

    return (this.restfulApiService.get(url) as Observable<any>).pipe(
      map(response => {
        const translations: LanguageTranslations = {};
        // Handle the specific API response format: { "response": { "KEY": "VALUE", ... } }
        if (response && response.response && typeof response.response === 'object') {
          // The response.response contains the flat key-value pairs we want
          Object.assign(translations, response.response);
         }
        // Fallback: Handle legacy format with objects array
        else if (response && response.response && response.response.objects) {
          response.response.objects.forEach((item: LanguageTranslationItem) => {
            translations[item.labelKey] = item.selectedLocaleValue;
          });
        }
        // Fallback: Handle array format
        else if (response && Array.isArray(response)) {
          response.forEach((item: LanguageTranslationItem) => {
            translations[item.labelKey] = item.selectedLocaleValue;
          });
        }
        // Fallback: Handle direct object format
        else if (response && typeof response === 'object') {
          Object.assign(translations, response);
        }
        else {
          console.warn('Unrecognized API response format:', response);
        }

        return translations;
      }),
      tap(translations => {
        // Store translations in localStorage and update the subject only if this is for the current language
        const currentLang = this.getCurrentLanguage();
        const currentLocale = convertTwoToThreeLetterCode(currentLang.code);
        this.storeTranslations(translations, locale);

        if (locale === currentLocale) {
          this.translationsSubject.next(translations);
        }
      }),
      catchError((error) => {
        console.warn('Failed to fetch translations from API, using stored translations:', error);
        const storedTranslations = this.getStoredTranslations(locale);
        return of(storedTranslations);
      })
    );
  }

  private getStoredLanguage(): Language | undefined {
    const storedCode = localStorage.getItem(this.STORAGE_KEY);
    if (storedCode) {
      return this.languages.find(lang => lang.code === storedCode);
    }

    const browserLang = navigator.language.split('-')[0];
    return this.languages.find(lang => lang.code === browserLang) || this.languages[0];
  }

  private initializeTranslations(): void {
    // Convert language code to the API format (e.g., 'en' -> 'eng')
    const currentLang = this.getCurrentLanguage();
    const apiLangCode = convertTwoToThreeLetterCode(currentLang.code);

    // Check if we need to fetch translations from API
    if (this.shouldFetchTranslations(apiLangCode)) {
      this.getLanguageTranslations().subscribe({
        next: (translations) => {
          console.log(`Successfully loaded ${Object.keys(translations).length} translations`);
        },
        error: (error) => {
          console.error('Failed to load translations:', error);
        }
      });
    } else {
      const storedTranslations = this.getStoredTranslations(apiLangCode);
      this.translationsSubject.next(storedTranslations);
    }
  }

  private shouldFetchTranslations(locale: string): boolean {
    const storedTranslations = this.getStoredTranslations(locale);

    // If no translations exist, we need to fetch
    if (Object.keys(storedTranslations).length === 0) {
      console.error('No stored translations found');
      return true;
    }

    // Check if translations are stale
    const timestamp = this.getStoredTimestamp(locale);
    if (!timestamp) {
      console.error('No timestamp found for stored translations');
      return true;
    }

    const now = Date.now();
    const isStale = (now - timestamp) > this.TRANSLATIONS_CACHE_DURATION;

    if (isStale) {
      console.log('Stored translations are stale');
      return true;
    }

    return false;
  }

  private storeTranslations(translations: LanguageTranslations, locale: string): void {
    try {
      const translationsKey = `${this.TRANSLATIONS_STORAGE_KEY_PREFIX}_${locale}`;
      const timestampKey = `${this.TRANSLATIONS_TIMESTAMP_KEY_PREFIX}_${locale}`;
      localStorage.setItem(translationsKey, JSON.stringify(translations));
      localStorage.setItem(timestampKey, Date.now().toString());
    } catch (error) {
      console.warn('Failed to store translations in localStorage:', error);
    }
  }

  private getStoredTranslations(locale: string): LanguageTranslations {
    try {
      const translationsKey = `${this.TRANSLATIONS_STORAGE_KEY_PREFIX}_${locale}`;
      const stored = localStorage.getItem(translationsKey);
      return stored ? JSON.parse(stored) : {};
    } catch (error) {
      console.warn('Failed to retrieve translations from localStorage:', error);
      return {};
    }
  }

  private getStoredTimestamp(locale: string): number | null {
    try {
      const timestampKey = `${this.TRANSLATIONS_TIMESTAMP_KEY_PREFIX}_${locale}`;
      const stored = localStorage.getItem(timestampKey);
      return stored ? parseInt(stored, 10) : null;
    } catch (error) {
      console.warn('Failed to retrieve timestamp from localStorage:', error);
      return null;
    }
  }

  translate(key: string): string {
    const translations = this.translationsSubject.value;
    return translations[key] || key;
  }

  refreshTranslations(): void {
    this.getLanguageTranslations().subscribe({
      next: (translations) => {
        console.log(`Successfully refreshed ${Object.keys(translations).length} translations`);
      },
      error: (error) => {
        console.error('Failed to refresh translations:', error);
      }
    });
  }

  forceInitializeTranslations(): void {
    console.log('Force initializing translations...');
    this.initializeTranslations();
  }

  hasValidTranslations(): boolean {
    const currentLang = this.getCurrentLanguage();
    const locale = convertTwoToThreeLetterCode(currentLang.code);
    const storedTranslations = this.getStoredTranslations(locale);
    if (Object.keys(storedTranslations).length === 0) {
      return false;
    }

    const timestamp = this.getStoredTimestamp(locale);
    if (!timestamp) {
      return false;
    }

    const now = Date.now();
    const isStale = (now - timestamp) > this.TRANSLATIONS_CACHE_DURATION;

    return !isStale;
  }

  /**
   * Search for languages using a keyword
   * @param keyword - The search keyword (e.g., "afr", "ger", "spa")
   * @returns Observable of LanguageSearchResponse containing language results
   */
  searchLanguages(keyword: string): Observable<LanguageSearchResponse> {
    return this.restfulApiService.get(`/v1/admin/search-language/${keyword}`) as Observable<LanguageSearchResponse>;
  }

  /**
   * Create or update a language
   * @param payload - The language data to create or update
   * @returns Observable of the API response
   */
  createUpdateLanguage(payload: CreateUpdateLanguagePayload): Observable<any> {
    return this.restfulApiService.post('/v1/admin/create-update-language', payload);
  }

  /**
   * Delete a language
   * @param uuid - The UUID of the language to delete
   * @returns Observable of the API response
   */
  deleteLanguage(uuid: string): Observable<any> {
    return this.restfulApiService.delete(`/v1/admin/delete-language/${uuid}`);
  }

  /**
   * Fetch all languages with their usage data
   */
  getLanguagesWithUsage(): Observable<LanguagesWithUsageResponse> {
    return this.restfulApiService.get('/v1/admin/languages-with-usage') as Observable<LanguagesWithUsageResponse>;
  }
}

